 
 
/**
 * Geolocation Service
 *
 * Integrates with the elBooks geolocation microservice via the API Gateway.
 * The gateway proxies requests to the geolocation microservice and handles authentication.
 */

// Use the API gateway (server) which proxies to geolocation service
/* v8 ignore next */
const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080'
const GEOLOCATION_PATH = '/geolocation'

// Currency mapping for fallback
const CURRENCY_MAP: Record<string, string> = {
  'IN': 'INR',
  'US': 'USD',
  'GB': 'GBP',
  'CA': 'CAD',
  'AU': 'AUD',
  'SG': 'SGD',
  'AE': 'AED',
  'DE': 'EUR',
  'FR': 'EUR',
  'JP': 'JPY',
  'CN': 'CNY',
}

export interface Country {
  identifier: string // ISO 3166-1 alpha-2 code
  name: string
  alternateName?: string // Fixed: was alternate_name
  image?: string // Flag emoji
  telephone?: string // International dialing code
  currencyCode?: string // Fixed: was currency_code
  postalCodeName?: string // What the country calls its postal code (e.g., "ZIP Code", "PIN Code", "Postcode")
  postalCodeExample?: string // Example postal code (e.g., "90210", "110001")
  postalCodeFormat?: string // Format description (e.g., "5 digits", "6 digits")
  postalCodeRegex?: string // Regex pattern for validation
  geo?: {
    latitude?: number
    longitude?: number
  }
  url?: string
}

export interface State {
  identifier: string // Full ISO 3166-2 code (e.g., "IN-AP")
  countryIdentifier: string // ISO 3166-1 alpha-2 code
  name: string
  administrativeType?: string
  taxCode?: string // GST state code for India
  parentIdentifier?: string
  geo?: {
    latitude?: number
    longitude?: number
  } | null
  isActive: boolean
  url?: string
}

export interface BusinessIdentifierType {
  type: string
  country_code: string
  display_name: string
  description?: string
  regex_pattern?: string
  example?: string
  is_required: boolean
  sort_order: number
  is_active: boolean
}

export interface BusinessIdentifiersResponse {
  identifierTypes: BusinessIdentifierType[]
  totalCount: number
}

export interface Timezone {
  identifier: string // IANA timezone identifier (e.g., "Asia/Kolkata")
  name: string
  utcOffset: string // e.g., "+05:30"
  utcOffsetSeconds: number
  dstOffset?: string
  dstOffsetSeconds?: number
  hasDst: boolean
  abbreviation: string
  dstAbbreviation?: string
  description: string
  isPrimary?: boolean
}

export interface PostalCode {
  id: number
  postalCode: string
  countryIdentifier: string
  latitude: number
  longitude: number
  placeName?: string
  adminName1?: string // State/Province
  adminCode1?: string
  adminName2?: string // County/District
  adminCode2?: string
  adminName3?: string // Municipality
  adminCode3?: string
  accuracy?: number
  dataSource: string
  boundarySource: string
}


interface PostalCodesResponse {
  postalCodes: PostalCode[]
  total?: number
}

// ============================================================================
// Angular-Style Service Cache (In-Memory Singleton)
// ============================================================================
// This cache persists for the lifetime of the page and is shared across all components.
// It clears on page reload. Multiple simultaneous requests are automatically deduplicated.

class GeolocationCache {
  // In-memory data cache (clears on page reload)
  private countriesCache: Country[] | null = null
  private statesCache = new Map<string, State[]>()

  // In-flight request tracking for deduplication
  private countriesPromise: Promise<Country[]> | null = null
  private statesPromises = new Map<string, Promise<State[]>>()

  // Countries cache
  getCountries(): Country[] | null {
    return this.countriesCache
  }

  setCountries(countries: Country[]): void {
    this.countriesCache = countries
  }

  /* v8 ignore next 3 */
  hasCountries(): boolean {
    return this.countriesCache !== null
  }

  getCountriesPromise(): Promise<Country[]> | null {
    return this.countriesPromise
  }

  setCountriesPromise(promise: Promise<Country[]> | null): void {
    this.countriesPromise = promise
  }

  // States cache
  getStates(countryCode: string): State[] | null {
    return this.statesCache.get(countryCode) ?? null
  }

  setStates(countryCode: string, states: State[]): void {
    this.statesCache.set(countryCode, states)
  }

  /* v8 ignore next 3 */
  hasStates(countryCode: string): boolean {
    return this.statesCache.has(countryCode)
  }

  getStatesPromise(countryCode: string): Promise<State[]> | null {
    return this.statesPromises.get(countryCode) ?? null
  }

  setStatesPromise(countryCode: string, promise: Promise<State[]> | null): void {
    if (promise === null) {
      this.statesPromises.delete(countryCode)
    } else {
      this.statesPromises.set(countryCode, promise)
    }
  }

  // Clear all caches (useful for testing or manual refresh)
  clearAll(): void {
    this.countriesCache = null
    this.statesCache.clear()
    this.countriesPromise = null
    this.statesPromises.clear()
  }

  // Get cache stats
  getStats(): { countries: boolean; statesCount: number } {
    return {
      countries: this.countriesCache !== null,
      statesCount: this.statesCache.size,
    }
  }
}

// Singleton instance - shared across entire application (like Angular service)
const cache = new GeolocationCache()

/**
 * Clear geolocation cache (for manual refresh or testing)
 */
export function clearGeolocationCache(): void {
  cache.clearAll()
}

/**
 * Get cache statistics
 */
export function getCacheStats(): { countries: boolean; statesCount: number } {
  return cache.getStats()
}

/**
 * Get cached countries synchronously (returns null if not cached yet)
 * Use this when you need synchronous access to countries data
 */
export function getCachedCountries(): Country[] | null {
  return cache.getCountries()
}

/**
 * Get cached states synchronously (returns null if not cached yet)
 * Use this when you need synchronous access to states data
 */
export function getCachedStates(countryCode: string): State[] | null {
  return cache.getStates(countryCode)
}

/**
 * Get all countries from the geolocation service (with caching)
 * Uses in-memory cache and request deduplication (Angular-style service pattern)
 */
export async function getCountries(): Promise<Country[]> {
  // Check in-memory cache first
  const cached = cache.getCountries()
  if (cached) {
    return cached
  }

  // If there's already a request in progress, return that promise (deduplication)
  const inFlightPromise = cache.getCountriesPromise()
  if (inFlightPromise) {
    return inFlightPromise
  }

  // No cache and no in-flight request - fetch from API
  const promise = (async () => {
    try {
      const response = await fetch(`${API_BASE}${GEOLOCATION_PATH}/countries`)
      if (!response.ok) {
        throw new Error(`Failed to fetch countries: ${response.statusText}`)
      }
      const data = await response.json() as Country[] | { countries?: Country[] }
      // Handle both array response and object response
      const countries: Country[] = Array.isArray(data) ? data : (data.countries ?? [])

      // Cache the result in memory
      cache.setCountries(countries)

      return countries
    } catch (error) {
      console.error('Failed to fetch countries:', error)
      /* v8 ignore next 2 -- network error fallback */
      // Return fallback data
      return getFallbackCountries()
    /* v8 ignore next 4 */
    } finally {
      // Clear the promise after completion
      cache.setCountriesPromise(null)
    }
  })()

  // Store the promise for deduplication
  cache.setCountriesPromise(promise)
  return promise
}

function pick<T>(obj: Record<string, unknown>, fallback: T, ...keys: string[]): T {
  for (const k of keys) {
    if (obj[k] !== undefined) {
      return obj[k] as T
    }
  }
  return fallback
}

interface RawState {
  identifier: string; name: string; geo?: unknown
  country_identifier?: string; countryIdentifier?: string
  administrative_type?: string; administrativeType?: string
  tax_code?: string; taxCode?: string
  parent_identifier?: string; parentIdentifier?: string
  is_active?: boolean; isActive?: boolean
}

function transformState(state: RawState): State {
  const raw = state as unknown as Record<string, unknown>
  return {
    identifier: state.identifier,
    countryIdentifier: pick<string>(raw, '', 'country_identifier', 'countryIdentifier'),
    name: state.name,
    administrativeType: pick<string>(raw, '', 'administrative_type', 'administrativeType'),
    taxCode: pick<string>(raw, '', 'tax_code', 'taxCode'),
    parentIdentifier: pick<string>(raw, '', 'parent_identifier', 'parentIdentifier'),
    geo: state.geo as { latitude?: number; longitude?: number } | null | undefined,
    isActive: pick<boolean>(raw, true, 'is_active', 'isActive'),
  }
}

/**
 * Get states/subdivisions for a country
 * Uses in-memory cache and request deduplication (Angular-style service pattern)
 */
export async function getStates(countryCode: string): Promise<State[]> {
  // Check in-memory cache first
  const cached = cache.getStates(countryCode)
  if (cached) {
    return cached
  }

  // If there's already a request in progress for this country, return that promise (deduplication)
  const inFlightPromise = cache.getStatesPromise(countryCode)
  if (inFlightPromise) {
    return inFlightPromise
  }

  // No cache and no in-flight request - fetch from API
  const promise = (async () => {
    try {
      const response = await fetch(`${API_BASE}${GEOLOCATION_PATH}/countries/${countryCode}/states`)
      if (!response.ok) {
        throw new Error(`Failed to fetch states: ${response.statusText}`)
      }
      const data = await response.json() as RawState[] | { states?: RawState[] }
      // Handle both array response and object response
      const rawStates: RawState[] = Array.isArray(data) ? data : (data.states ?? [])

      // Transform snake_case to camelCase
      const states: State[] = rawStates.map(transformState)

      // Cache the result in memory
      cache.setStates(countryCode, states)

      return states
    } catch (error) {
      console.error(`Failed to fetch states for ${countryCode}:`, error)
      // Return fallback data for India
      if (countryCode === 'IN') {
        return getFallbackIndianStates()
      }
      /* v8 ignore next */
      return []
    /* v8 ignore next 4 */
    } finally {
      // Clear the promise after completion
      cache.setStatesPromise(countryCode, null)
    }
  })()

  // Store the promise for deduplication
  cache.setStatesPromise(countryCode, promise)
  return promise
}

interface RawTimezone {
  identifier: string; name: string; abbreviation?: string; description?: string
  utc_offset?: string; utcOffset?: string
  utc_offset_seconds?: number; utcOffsetSeconds?: number
  dst_offset?: string; dstOffset?: string
  dst_offset_seconds?: number; dstOffsetSeconds?: number
  observes_dst?: boolean; hasDst?: boolean
  dst_abbreviation?: string; dstAbbreviation?: string
  is_primary?: boolean; isPrimary?: boolean
}

function transformTimezone(tz: RawTimezone): Timezone {
  const raw = tz as unknown as Record<string, unknown>
  return {
    identifier: tz.identifier,
    name: tz.name,
    utcOffset: pick<string>(raw, '', 'utc_offset', 'utcOffset'),
    utcOffsetSeconds: pick<number>(raw, 0, 'utc_offset_seconds', 'utcOffsetSeconds'),
    dstOffset: pick<string>(raw, '', 'dst_offset', 'dstOffset'),
    dstOffsetSeconds: pick<number>(raw, 0, 'dst_offset_seconds', 'dstOffsetSeconds'),
    hasDst: pick<boolean>(raw, false, 'observes_dst', 'hasDst'),
    /* v8 ignore next 3 */
    abbreviation: tz.abbreviation ?? '',
    dstAbbreviation: pick<string>(raw, '', 'dst_abbreviation', 'dstAbbreviation'),
    description: tz.description ?? '',
    isPrimary: pick<boolean>(raw, false, 'is_primary', 'isPrimary'),
  }
}

/**
 * Get all timezones from the geolocation service
 */
export async function getTimezones(): Promise<Timezone[]> {
  try {
    const response = await fetch(`${API_BASE}${GEOLOCATION_PATH}/timezones`)
    if (!response.ok) {
      throw new Error(`Failed to fetch timezones: ${response.statusText}`)
    }
    const data: unknown = await response.json()
    // API returns array directly, not wrapped in {timezones: [...]}
    const raw = data as RawTimezone[] | { timezones?: RawTimezone[] }
    /* v8 ignore next */
    const timezones: RawTimezone[] = Array.isArray(raw) ? raw : (raw.timezones ?? [])

    // Transform snake_case API response to camelCase for TypeScript
    return timezones.map(transformTimezone)
  } catch (error) {
    console.error('Failed to fetch timezones:', error)
    return []
  }
}

/**
 * Get timezones for a specific country
 */
export async function getCountryTimezones(countryCode: string): Promise<Timezone[]> {
  try {
    const response = await fetch(`${API_BASE}${GEOLOCATION_PATH}/countries/${countryCode}/timezones`)
    if (!response.ok) {
      throw new Error(`Failed to fetch timezones for ${countryCode}: ${response.statusText}`)
    }
    const data: unknown = await response.json()
    // API returns array directly, not wrapped in {timezones: [...]}
    const raw = data as RawTimezone[] | { timezones?: RawTimezone[] }
    /* v8 ignore next */
    const timezones: RawTimezone[] = Array.isArray(raw) ? raw : (raw.timezones ?? [])

    // Transform snake_case API response to camelCase for TypeScript
    return timezones.map(transformTimezone)
  } catch (error) {
    console.error(`Failed to fetch timezones for ${countryCode}:`, error)
    return []
  }
}

/**
 * Get country by code
 */
export async function getCountryByCode(code: string): Promise<Country | undefined> {
  const countries = await getCountries()
  return countries.find(c => c.identifier === code)
}

/**
 * Get state by code
 */
export async function getStateByCode(countryCode: string, stateCode: string): Promise<State | undefined> {
  const states = await getStates(countryCode)
  return states.find(s => s.identifier === stateCode)
}

/**
 * Get default currency for a country
 */
export function getCountryCurrency(countryCode: string): string {
  return CURRENCY_MAP[countryCode] ?? 'INR'
}

/**
 * Search postal codes by postal code string
 */
export async function searchPostalCodes(
  countryCode: string,
  postalCodeQuery: string,
  limit: number = 10
): Promise<PostalCode[]> {
  if (!postalCodeQuery || postalCodeQuery.length < 2) {
    return []
  }

  try {
    const params = new URLSearchParams({
      query: postalCodeQuery,
      limit: limit.toString(),
    })
    const response = await fetch(
      `${API_BASE}${GEOLOCATION_PATH}/countries/${countryCode}/postal-codes?${String(params)}`
    )
    if (!response.ok) {
      throw new Error(`Failed to search postal codes: ${response.statusText}`)
    }
    const data = await response.json() as PostalCodesResponse
    return data.postalCodes ?? []
  } catch (error) {
    console.error('Failed to search postal codes:', error)
    return []
  }
}

/**
 * Search postal codes by place name
 */
export async function searchPostalCodesByPlace(
  searchTerm: string,
  countryCode?: string,
  limit: number = 10
): Promise<PostalCode[]> {
  if (!searchTerm || searchTerm.length < 3) {
    return []
  }

  try {
    const params = new URLSearchParams({
      search_term: searchTerm,
      limit: limit.toString(),
    })
    if (countryCode) {
      params.append('country_code', countryCode)
    }
    const response = await fetch(
      `${API_BASE}${GEOLOCATION_PATH}/postal-codes/search?${String(params)}`
    )
    if (!response.ok) {
      throw new Error(`Failed to search postal codes by place: ${response.statusText}`)
    }
    const data = await response.json() as PostalCodesResponse
    /* v8 ignore next */
    return data.postalCodes ?? []
  } catch (error) {
    console.error('Failed to search postal codes by place:', error)
    return []
  }
}

/**
 * Get postal code details
 */
export async function getPostalCode(
  countryCode: string,
  postalCode: string
): Promise<PostalCode | undefined> {
  try {
    const response = await fetch(
      `${API_BASE}${GEOLOCATION_PATH}/countries/${countryCode}/postal-codes/${postalCode}`
    )
    if (!response.ok) {
      throw new Error(`Failed to get postal code: ${response.statusText}`)
    }
    const data = await response.json() as PostalCode
    return data
  } catch (error) {
    console.error('Failed to get postal code:', error)
    return undefined
  }
}

/**
 * Get business identifiers for a country
 * This returns all valid business identifier types for the specified country,
 * including identifiers inherited from parent countries for territories
 */
export async function getBusinessIdentifiers(countryCode: string): Promise<BusinessIdentifierType[]> {
  try {
    const response = await fetch(
      `${API_BASE}${GEOLOCATION_PATH}/countries/${countryCode}/business-identifiers`
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch business identifiers: ${response.statusText}`)
    }
    const data = await response.json() as BusinessIdentifiersResponse
    return data.identifierTypes ?? []
  } catch (error) {
    console.error(`Failed to fetch business identifiers for ${countryCode}:`, error)
    return []
  }
}

// ============================================================================
// Fallback Data (Emergency only - when geolocation API is completely down)
// ============================================================================
// IMPORTANT: This is a MINIMAL fallback for emergency use only.
// In normal operation, ALL countries are loaded from the geolocation API/database.
// The geolocation database contains ~195 countries with complete data.
// If you need to add countries, add them to the geolocation database, NOT here.
// ============================================================================

function getFallbackCountries(): Country[] {
  console.warn('⚠️  Using emergency fallback countries - geolocation API is unavailable!')
  console.warn('This is a minimal fallback list. Please ensure the geolocation service is running.')
  console.warn('Expected API endpoint:', `${API_BASE}${GEOLOCATION_PATH}/countries`)

  // Minimal set for emergency fallback only
  return [
    { identifier: 'US', name: 'United States', currencyCode: 'USD', image: '🇺🇸', telephone: '+1' },
    { identifier: 'IN', name: 'India', currencyCode: 'INR', image: '🇮🇳', telephone: '+91' },
    { identifier: 'GB', name: 'United Kingdom', currencyCode: 'GBP', image: '🇬🇧', telephone: '+44' },
  ]
}

function getFallbackIndianStates(): State[] {
  return [
    { identifier: 'IN-AN', name: 'Andaman and Nicobar Islands', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '35', isActive: true },
    { identifier: 'IN-AP', name: 'Andhra Pradesh', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '37', isActive: true },
    { identifier: 'IN-AR', name: 'Arunachal Pradesh', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '12', isActive: true },
    { identifier: 'IN-AS', name: 'Assam', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '18', isActive: true },
    { identifier: 'IN-BR', name: 'Bihar', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '10', isActive: true },
    { identifier: 'IN-CH', name: 'Chandigarh', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '04', isActive: true },
    { identifier: 'IN-CT', name: 'Chhattisgarh', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '22', isActive: true },
    { identifier: 'IN-DH', name: 'Dadra and Nagar Haveli and Daman and Diu', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '26', isActive: true },
    { identifier: 'IN-DL', name: 'Delhi', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '07', isActive: true },
    { identifier: 'IN-GA', name: 'Goa', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '30', isActive: true },
    { identifier: 'IN-GJ', name: 'Gujarat', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '24', isActive: true },
    { identifier: 'IN-HR', name: 'Haryana', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '06', isActive: true },
    { identifier: 'IN-HP', name: 'Himachal Pradesh', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '02', isActive: true },
    { identifier: 'IN-JK', name: 'Jammu and Kashmir', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '01', isActive: true },
    { identifier: 'IN-JH', name: 'Jharkhand', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '20', isActive: true },
    { identifier: 'IN-KA', name: 'Karnataka', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '29', isActive: true },
    { identifier: 'IN-KL', name: 'Kerala', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '32', isActive: true },
    { identifier: 'IN-LA', name: 'Ladakh', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '38', isActive: true },
    { identifier: 'IN-LD', name: 'Lakshadweep', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '31', isActive: true },
    { identifier: 'IN-MP', name: 'Madhya Pradesh', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '23', isActive: true },
    { identifier: 'IN-MH', name: 'Maharashtra', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '27', isActive: true },
    { identifier: 'IN-MN', name: 'Manipur', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '14', isActive: true },
    { identifier: 'IN-ML', name: 'Meghalaya', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '17', isActive: true },
    { identifier: 'IN-MZ', name: 'Mizoram', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '15', isActive: true },
    { identifier: 'IN-NL', name: 'Nagaland', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '13', isActive: true },
    { identifier: 'IN-OR', name: 'Odisha', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '21', isActive: true },
    { identifier: 'IN-PY', name: 'Puducherry', countryIdentifier: 'IN', administrativeType: 'Union Territory', taxCode: '34', isActive: true },
    { identifier: 'IN-PB', name: 'Punjab', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '03', isActive: true },
    { identifier: 'IN-RJ', name: 'Rajasthan', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '08', isActive: true },
    { identifier: 'IN-SK', name: 'Sikkim', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '11', isActive: true },
    { identifier: 'IN-TN', name: 'Tamil Nadu', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '33', isActive: true },
    { identifier: 'IN-TG', name: 'Telangana', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '36', isActive: true },
    { identifier: 'IN-TR', name: 'Tripura', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '16', isActive: true },
    { identifier: 'IN-UP', name: 'Uttar Pradesh', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '09', isActive: true },
    { identifier: 'IN-UT', name: 'Uttarakhand', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '05', isActive: true },
    { identifier: 'IN-WB', name: 'West Bengal', countryIdentifier: 'IN', administrativeType: 'State', taxCode: '19', isActive: true },
  ]
}
